import React from "react";
import { Box, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";

interface StyledDropzoneProps {
    onAddFile: (acceptedFiles: File[]) => void;
    disabled: boolean;
}

const StyledDropzone: React.FC<StyledDropzoneProps> = (props) => {
    const maxSize = 1048576 * 10;

    const onDrop = React.useCallback(
        (acceptedFiles: File[]) => {
            props.onAddFile([...acceptedFiles]);
        },
        [props],
    );

    const { isDragAccept, isFocused, getRootProps, getInputProps, isDragReject, fileRejections } =
        useDropzone({
            onDrop,
            accept: {
                "application/pdf": [".pdf"],
            },
            minSize: 0,
            maxSize,
            multiple: false,
            maxFiles: 1,
            disabled: props.disabled,
        });

    const isFileTooLarge = fileRejections.length > 0 && fileRejections[0].file.size > maxSize;

    const getBorderColor = (): string => {
        if (isFocused) return "darkblue";
        else if (isDragAccept) return "darkgreen";
        else if (isDragReject) return "darkred";
        return "gray";
    };

    const getBackgroundColor = (): string => {
        if (isFocused) return "aliceblue";
        else if (isDragAccept) return "honeydew";
        else if (isDragReject) return "mistyrose";
        return "lightgray";
    };

    return (
        <Box
            className="container"
            sx={{
                borderColor: getBorderColor(),
                backgroundColor: getBackgroundColor(),
                m: 2,
                p: 8,
                borderStyle: "dashed",
                borderWidth: 2,
                borderRadius: 3,
                "&:hover": {
                    border: "2px dashed darkblue",
                    color: "black",
                    backgroundColor: "aliceblue",
                },
            }}
            display="flex"
            justifyContent="center"
            alignItems="center"
        >
            <div {...getRootProps()}>
                <input {...getInputProps()} />
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexDirection="column"
                >
                    {props.disabled && (
                        <Typography align="center" variant="h6">
                            Document selected — click <strong>Upload Document</strong> to proceed,
                            or <strong>Clear</strong> to choose a different document.
                        </Typography>
                    )}
                    {!props.disabled && (
                        <Typography align="center" variant="h6">
                            Drop a PDF document here or click to select.
                        </Typography>
                    )}
                    {!props.disabled && (
                        <Typography align="center" variant="body2">
                            Only PDF documents are accepted (max size: 10 MB).
                        </Typography>
                    )}
                    {!props.disabled && isDragReject && (
                        <Typography align="center" variant="body1" color="error">
                            Invalid document type — only PDF documents are accepted.
                        </Typography>
                    )}
                    {!props.disabled && isFileTooLarge && (
                        <Typography align="center" variant="body1" color="error">
                            Document is too large (must be less than 10 MB).
                        </Typography>
                    )}
                </Box>
            </div>
        </Box>
    );
};

export default StyledDropzone;
